<script setup lang="ts">
  import { computed, useId } from 'vue';
  import { useLocale } from '@tundr/i18n';
  import { TUNDR_ICONS } from '@tundr/theme-assets/tundr-icons/tundr-icons-icons';
  import { FontIcon } from '../FontIcon';
  import { Tooltip } from '../Tooltip';
  import { visuallyHiddenClass } from '../_shared/styles/visually-hidden.css';
  import { FilterVariant, type FilterProps } from './Filter.types';
  import {
    wrapperDefaultActiveClass,
    wrapperDefaultInactiveClass,
    wrapperTableActiveClass,
    wrapperTableInactiveClass,
    wrapperDisabledClass,
    mainButtonClass,
    mainButtonDisabledClass,
    labelWrapperVariantClasses,
    labelWrapperDisabledClass,
    labelWrapperInactiveVariantClasses,
    valueWrapperVariantClasses,
    valueWrapperInactiveVariantClasses,
    valueWrapperDisabledClass,
    valueWrapperWithRemoveClass,
    removeAreaVariantClasses,
    removeAreaInactiveVariantClasses,
    valueTextContainerClass,
    valueTextClass,
    countBadgeClass,
    countBadgeDisabledClass,
    chevronClass,
    dividerClass,
    dividerLineVariantClasses,
    removeButtonClass,
  } from './Filter.css';

  const props = withDefaults(defineProps<FilterProps>(), {
    removable: true,
    inactive: false,
    disabled: false,
    variant: FilterVariant.Default,
  });

  const { t } = useLocale('ds-components');

  const uid = useId();
  const resolvedTestId = computed(() => props.testId ?? `ds-filter-${uid}`);

  const emit = defineEmits<{
    click: [event: MouseEvent];
    remove: [event: MouseEvent];
  }>();

  const hasValue = computed(() => !!props.value);
  const hasCount = computed(() => props.count != null);
  const isFilled = computed(() => hasValue.value || hasCount.value);

  const rootClass = computed(() => {
    if (props.disabled) return wrapperDisabledClass;
    if (props.variant === FilterVariant.Table) {
      return props.inactive
        ? wrapperTableInactiveClass
        : wrapperTableActiveClass;
    }
    return props.inactive
      ? wrapperDefaultInactiveClass
      : wrapperDefaultActiveClass;
  });

  const dividerLineClass = computed(() => {
    if (props.variant === FilterVariant.Default) {
      return props.inactive
        ? dividerLineVariantClasses['default-inactive']
        : dividerLineVariantClasses[FilterVariant.Default];
    }
    return props.inactive
      ? dividerLineVariantClasses['table-inactive']
      : dividerLineVariantClasses[FilterVariant.Table];
  });

  const labelWrapperClass = computed(() => {
    if (props.disabled) return labelWrapperDisabledClass;
    return props.inactive
      ? labelWrapperInactiveVariantClasses[props.variant]
      : labelWrapperVariantClasses[props.variant];
  });

  const showRemove = computed(
    () => props.removable && isFilled.value && !props.disabled,
  );

  const valueWrapperClass = computed(() => [
    props.disabled
      ? valueWrapperDisabledClass
      : props.inactive
        ? valueWrapperInactiveVariantClasses[props.variant]
        : valueWrapperVariantClasses[props.variant],
    showRemove.value ? valueWrapperWithRemoveClass : null,
  ]);

  const removeAreaClass = computed(() =>
    props.inactive
      ? removeAreaInactiveVariantClasses[props.variant]
      : removeAreaVariantClasses[props.variant],
  );

  const countAnnouncement = computed(() =>
    props.countTooltip
      ? t('filter.count_aria_label', {
          count: props.count,
          values: props.countTooltip.split('\n').join(', '),
        })
      : undefined,
  );

  const onClick = (event: MouseEvent) => {
    if (props.disabled) return;
    emit('click', event);
  };

  const onRemove = (event: MouseEvent) => {
    event.stopPropagation();
    emit('remove', event);
  };
</script>

<template>
  <div
    role="group"
    :aria-label="t('filter.aria_label', { label })"
    :class="rootClass"
    :data-testid="resolvedTestId"
  >
    <button
      type="button"
      :class="[mainButtonClass, disabled ? mainButtonDisabledClass : null]"
      :disabled="disabled || undefined"
      :data-testid="`${resolvedTestId}-main`"
      @click="onClick"
    >
      <span :class="labelWrapperClass" :data-testid="`${resolvedTestId}-label`"
        >{{ label }}{{ isFilled ? ':' : '' }}</span
      >
      <span
        :class="valueWrapperClass"
        :data-testid="`${resolvedTestId}-value-wrapper`"
      >
        <span
          v-if="hasValue && !hasCount"
          :class="valueTextContainerClass"
          :style="valueMaxWidth ? { maxWidth: valueMaxWidth } : undefined"
          :title="value"
          :data-testid="`${resolvedTestId}-value`"
        >
          <span :class="valueTextClass">{{ value }}</span>
        </span>
        <Tooltip v-if="hasCount" :description="countTooltip">
          <span
            :class="[countBadgeClass, disabled ? countBadgeDisabledClass : null]"
            :data-testid="`${resolvedTestId}-count`"
          >
            <span :aria-hidden="countAnnouncement ? 'true' : undefined">{{
              count
            }}</span>
            <span v-if="countAnnouncement" :class="visuallyHiddenClass">{{
              countAnnouncement
            }}</span>
          </span>
        </Tooltip>
        <span
          :class="chevronClass"
          aria-hidden="true"
          :data-testid="`${resolvedTestId}-chevron`"
        >
          <slot name="icon">
            <FontIcon :icon-name="TUNDR_ICONS.CHEVRON_DOWN" />
          </slot>
        </span>
      </span>
    </button>
    <span v-if="showRemove" :class="removeAreaClass">
      <span
        :class="dividerClass"
        aria-hidden="true"
        :data-testid="`${resolvedTestId}-divider`"
      >
        <span :class="dividerLineClass" />
      </span>
      <button
        type="button"
        :class="removeButtonClass"
        :aria-label="t('filter.remove_aria_label', { label })"
        :data-testid="`${resolvedTestId}-remove`"
        @click="onRemove"
      >
        <FontIcon :icon-name="TUNDR_ICONS.CLOSE" aria-hidden="true" />
      </button>
    </span>
  </div>
</template>
